import get from 'lodash/get';

import { loadCookie, saveCookie } from 'globals/cookieHelper';
import {
  handleStartChat,
  handleStartConnection,
  handleInitCustomerData,
  fetchSessionState,
  updateLastActivity,
} from '../../chat/app/ChatApp/actions';
import API from '../../../api/api';
import * as chatSelectors from '../../chat/app/ChatApp/selectors';
import { handleJoinConversationById } from '../../chat/app/ChatApp/actions_routing';
import * as selectors from './selectors';
import * as vcTimerHelpers from '../../chat/conversation/ChatConversationVolumeControlInput/helpers';

export const APP_ROUTE_TO_KUSTY = 'app-route-to-kusty';
export const APP_ROUTE_TO_NONE = 'app-route-to-none';
export const APP_ROUTE_TO_APP = 'app-route-to-app';
export const APP_START = 'app-start';
export const APP_STOP = 'app-stop';
export const APP_RTL = 'app-rtl';
export const APP_ONLINE = 'app-online';
export const APP_OFFLINE = 'app-offline';
export const APP_BLACKLIST = 'app-blacklist';

export const routeToKusty = () => ({ type: APP_ROUTE_TO_KUSTY });
export const routeToNone = () => ({ type: APP_ROUTE_TO_NONE });
export const routeToApp = () => ({ type: APP_ROUTE_TO_APP });
export const setAppBlackList = payload => ({ type: APP_BLACKLIST, payload });

export const startApp = () => ({ type: APP_START });
export const stopApp = () => ({ type: APP_STOP });

export const setAppStylesRTL = () => ({ type: APP_RTL });

export const setAppOnline = () => ({ type: APP_ONLINE });
export const setAppOffline = () => ({ type: APP_OFFLINE });

export const handleRouteToKusty = () => dispatch => {
  saveCookie('route', 'kusty');
  dispatch(routeToKusty());
};

export const handleRouteToNone = () => dispatch => {
  saveCookie('route', 'none');
  dispatch(routeToNone());
};

export const handleRouteToApp = () => dispatch => {
  const maxAge = 3600; // one hour
  saveCookie('route', 'app', { maxAge });
  dispatch(routeToApp());
  dispatch(fetchSessionState());
};

export const handleRouteToAppAndSession = sessionId => dispatch => {
  const maxAge = 3600; // one hour
  saveCookie('route', 'app', { maxAge });
  dispatch(handleJoinConversationById(sessionId));
  dispatch(routeToApp());
  dispatch(fetchSessionState());
};

export const handleCampaignClick = sessionId => dispatch => {
  dispatch(handleJoinConversationById(sessionId));
  dispatch(handleRouteToApp());
};

export const handleIconClick = () => (dispatch, getState) => {
  const campaigns = chatSelectors.getCampaignMessages(getState());
  const campaignId = get(campaigns.first(), 'sessionId');
  if (campaignId) {
    dispatch(handleCampaignClick(campaignId));
  } else {
    dispatch(handleRouteToApp());
  }
};

export const handleStopApp = () => dispatch => {
  dispatch(stopApp());
  dispatch(handleRouteToNone());
};

export const handleOpenApp = () => dispatch => {
  dispatch(handleRouteToApp());
};

export const handleOpenAppAndSession = sessionId => dispatch => {
  dispatch(handleRouteToAppAndSession(sessionId));
};

export const handleStartAppInRTL = () => dispatch => {
  dispatch(setAppStylesRTL());
};

export const handleCloseApp = () => dispatch => {
  const noIcon = loadCookie('noIcon');
  if (noIcon) {
    dispatch(handleRouteToNone());
  } else {
    dispatch(handleRouteToKusty());
  }
};

export const maybeConnectAndFetchSessions = () => async (dispatch, getState) => {
  let serverActivity;
  try {
    serverActivity = await API.getCustomerStats(false);
  } catch (err) {
    return;
  }
  const localActivity = chatSelectors.getLastActivity(getState());
  const missedActivity = new Date(serverActivity.activity).valueOf() > new Date(localActivity).valueOf();

  if (missedActivity || (!localActivity && serverActivity)) {
    dispatch(handleStartConnection(serverActivity));
    dispatch(handleInitCustomerData({}));
    dispatch(updateLastActivity(serverActivity.activity));
  } else {
    vcTimerHelpers.resumeVcTimers();
  }
};

export const handleFirstRoute = () => dispatch => {
  const sessionId = loadCookie('sessionId');
  const hasExistingSession = sessionId && sessionId !== 'new';
  const route = loadCookie('route');
  const noIcon = loadCookie('noIcon');

  if (noIcon && route === 'none') {
    dispatch(handleRouteToNone());
  } else if (noIcon && hasExistingSession) {
    dispatch(handleRouteToApp());
  } else if (route === 'app') {
    dispatch(handleRouteToApp());
  } else if (!noIcon) {
    dispatch(handleRouteToKusty());
  } else {
    dispatch(handleRouteToNone());
  }
};

export const handleAppInit = cb => (dispatch, getState) => {
  return dispatch(
    handleStartChat(({ enabled, settings, isInsideBusinessHours }) => {
      const state = getState();
      const lang = API.getLangOverride();
      const isLangRTL = API.isLangOverrideRTL();
      const form = API.getFormOverride();
      const activeSessionCount = chatSelectors.getActiveSessionCount(state);
      const isDomainWhitelisted = !selectors.getAppIsBlacklisted(state);
      const embedCallback = get(window, 'Kustomer._startCallback');

      if (enabled) {
        if (isLangRTL) {
          API.setWidgetToRTL();
          dispatch(handleStartAppInRTL());
        }

        dispatch(handleFirstRoute());
      }

      // Hack for declaring the cb in the embed code ;_;
      if (embedCallback && !cb) {
        window.Kustomer._startCallback({
          enabled,
          settings,
          isInsideBusinessHours,
          activeSessionCount,
          isLangRTL,
          lang,
          form,
          isDomainWhitelisted,
        });
        window.Kustomer._startCallback = null;
      }

      if (cb) {
        cb({
          enabled,
          settings,
          isInsideBusinessHours,
          activeSessionCount,
          isLangRTL,
          lang,
          form,
          isDomainWhitelisted,
        });
      }
    }),
  );
};

export const handleStartApp = cb => dispatch => {
  dispatch(startApp());
  dispatch(handleAppInit(cb));
};

export const handleRestartApp = cb => dispatch => {
  dispatch(stopApp());
  dispatch(handleStartApp(cb));
};

export const handleStopAppIfDisconnected = () => (dispatch, getState) => {
  const activity = chatSelectors.getLastActivity(getState());
  const shouldMaintainActiveChats = API.shouldMaintainActiveChats(activity);
  const shouldStopApp = !API.isRTCAlive() && !shouldMaintainActiveChats;

  if (shouldStopApp) {
    API.disconnect();
    dispatch(stopApp());
  }
};

export const getCompanySettings = () => (_, getState) => {
  const state = getState();
  return Promise.resolve(chatSelectors.getCompany(state));
};
